import React, { useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  Handle,
  Position,
  Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useFetchSlackChannels, useFetchWorkflows, useFetchAdminUsers } from '../../../apolloClient/hooks/general/fetchHook';
import { CREATE_TEMPLATE } from '../../../apolloClient/operation/mutations/mutation';
import { TemplateInput, NotificationEvent, RecipientInput } from '../../../types/graphQlQuery.interface.d';

const triggerOptions = [
  { value: NotificationEvent.NEW_USER_CREATED, label: 'New User Created' },
  { value: NotificationEvent.PROCEEDED_TO_PROJECT_PLANNING, label: 'Proceeded to Project Planning' },
  { value: NotificationEvent.UPLOADED_INSURANCE_SCOPE, label: 'Uploaded Insurance Scope' },
  { value: NotificationEvent.PROCEEDED_TO_ORDERING, label: 'Proceeded to Ordering' },
  { value: NotificationEvent.SIGNED_ESTIMATE, label: 'Signed Estimate' },
  { value: NotificationEvent.MADE_PAYMENT, label: 'Made Payment' },
  { value: NotificationEvent.PROCEEDED_TO_CONSTRUCTION, label: 'Proceeded to Construction' },
  { value: NotificationEvent.PROCEEDED_TO_COMPLETION, label: 'Proceeded to Completion' },
  { value: NotificationEvent.TEST_NOTIFICATION, label: 'Test Notification' }
];

const actionTypes = [
  { value: 'email', label: 'Send Email' },
  { value: 'slack', label: 'Send Slack Message' }
];

type NotificationAction = {
  type: 'email' | 'slack';
  list: string[];
};

// Keep the old format for internal workflow management
type WorkflowData = {
  triggerId: string;
  triggerType: NotificationEvent;
  actions: NotificationAction[];
};

// New format for the mutation
type TemplateWorkflowData = {
  eventType: NotificationEvent;
  templateBody?: string;
  active: boolean;
  recipients: RecipientInput[];
};

const TriggerNode = ({ data }: { data: any }) => {
  const isInactive = data.active === false;
  
  return (
    <div style={{
      padding: '15px',
      borderRadius: '8px',
      background: isInactive ? '#f5f5f5' : 'white',
      border: `2px solid ${isInactive ? '#ccc' : '#2196f3'}`,
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      minWidth: '200px',
      position: 'relative',
      opacity: isInactive ? 0.6 : 1
    }}>
      <Handle type="source" position={Position.Bottom} />
      
      {/* Cross button for deactivating trigger */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          data.onToggleActive && data.onToggleActive(data.id);
        }}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0',
          color: isInactive ? '#999' : '#2196f3',
          fontSize: '16px',
          fontWeight: 'bold',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%'
        }}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title={isInactive ? "Reactivate trigger" : "Deactivate trigger"}
      >
        {isInactive ? '↻' : '×'}
      </button>
      
      <div>
        <div style={{ 
          fontWeight: 'bold', 
          color: isInactive ? '#999' : '#2196f3', 
          marginBottom: '8px' 
        }}>
          Event Trigger {isInactive && '(Inactive)'}
        </div>
        <select 
          value={data.triggerType || ''}
          onChange={(evt) => data.onTriggerChange(evt.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            marginBottom: '8px'
          }}
        >
          <option value="">Select Trigger</option>
          {triggerOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        {data.triggerType && (
          <div>
            <div style={{ fontWeight: 'bold', color: '#2196f3', marginBottom: '8px' }}>Add Action</div>
            <select
              value=""
              onChange={(evt) => data.onAddAction(evt.target.value, data.id)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
            >
              <option value="">Select Action Type</option>
              {actionTypes.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

const ActionNode = ({ data }: { data: any }) => {
  const [error, setError] = React.useState<string>('');

  const handleListChange = (value: string) => {
    const list = value.split(',').map(item => item.trim());
    
    if (data.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = list.every(email => emailRegex.test(email));
      setError(isValid ? '' : 'Invalid email format');
    }
    
    data.onConfigChange({ list });
  };

  const handleChannelSelect = (channelName: string) => {
    const currentList = data.config?.list || [];
    const isSelected = currentList.includes(channelName);
    
    let newList;
    if (isSelected) {
      // Remove channel if already selected
      newList = currentList.filter(channel => channel !== channelName);
    } else {
      // Add channel if not selected
      newList = [...currentList, channelName];
    }
    
    data.onConfigChange({ list: newList });
  };

  const handleRemoveChannel = (channelToRemove: string) => {
    const currentList = data.config?.list || [];
    const currentInactive = data.config?.inactiveList || [];
    
    // Instead of removing, add to inactive list
    const newList = currentList.filter(channel => channel !== channelToRemove);
    const newInactiveList = [...currentInactive];
    
    if (!newInactiveList.includes(channelToRemove)) {
      newInactiveList.push(channelToRemove);
    }
    
    data.onConfigChange({ 
      list: newList,
      inactiveList: newInactiveList 
    });
  };

  return (
    <div style={{
      padding: '15px',
      borderRadius: '8px',
      background: 'white',
      border: `2px solid ${error ? '#f44336' : '#4caf50'}`,
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      minWidth: '200px',
      // position: 'relative'
    }}>
      <Handle type="target" position={Position.Top} />
      <div style={{ fontWeight: 'bold', color: '#4caf50', marginBottom: '8px' }}>{data.label}</div>
      
      {data.type === 'slack' && data.slackChannels && data.slackChannels.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) {
                handleChannelSelect(e.target.value);
                e.target.value = ""; // Reset dropdown
              }
            }}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '12px',
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            <option value="">Select a channel to add/remove</option>
            {data.slackChannels?.map((channel: string, index: number) => {
              const isSelected = (data.config?.list || []).includes(channel);
              return (
                <option 
                  key={index} 
                  value={channel}
                  style={{
                    fontWeight: isSelected ? 'bold' : 'normal',
                    color: isSelected ? '#2196f3' : '#333'
                  }}
                >
                  {isSelected ? `✓ ${channel} (selected)` : channel}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {data.type === 'email' && data.adminEmails && data.adminEmails.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) {
                handleChannelSelect(e.target.value);
                e.target.value = ""; // Reset dropdown
              }
            }}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '12px',
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            <option value="">Select an email to add/remove</option>
            {data.adminEmails?.map((email: string, index: number) => {
              const isSelected = (data.config?.list || []).includes(email);
              return (
                <option 
                  key={index} 
                  value={email}
                  style={{
                    fontWeight: isSelected ? 'bold' : 'normal',
                    color: isSelected ? '#2196f3' : '#333'
                  }}
                >
                  {isSelected ? `✓ ${email} (selected)` : email}
                </option>
              );
            })}
          </select>
        </div>
      )}
      
      {data.type === 'slack' ? (
        // Selected channels display for Slack
        <div>
          {data.config?.list && data.config.list.length > 0 ? (
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px',
              minHeight: '60px',
              backgroundColor: '#fff'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Selected Channels:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {data.config.list.map((channel: string, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#e3f2fd',
                      padding: '4px 8px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      border: '1px solid #2196f3'
                    }}
                  >
                    <span style={{ marginRight: '6px' }}>{channel}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveChannel(channel);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        color: '#2196f3',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{
              border: '1px dashed #ddd',
              borderRadius: '4px',
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              fontSize: '12px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              No channels selected. Use the dropdown above to select channels.
            </div>
          )}
        </div>
      ) : (
        // Email recipients display with same badge format
        <div>
          {data.config?.list && data.config.list.length > 0 ? (
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px',
              minHeight: '60px',
              backgroundColor: '#fff'
            }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Selected Emails:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {data.config.list.map((email: string, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#e8f5e8',
                      padding: '4px 8px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      border: '1px solid #4caf50'
                    }}
                  >
                    <span style={{ marginRight: '6px' }}>{email}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveChannel(email);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        color: '#4caf50',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2e7d32'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{
              border: '1px dashed #ddd',
              borderRadius: '4px',
              padding: '20px',
              textAlign: 'center',
              color: '#666',
              fontSize: '12px',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              No emails selected. Use the dropdown above to select emails.
            </div>
          )}
          {error && (
            <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>{error}</div>
          )}
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  triggerNode: TriggerNode,
  actionNode: ActionNode,
};

export const NotificationWorkflow: React.FC = () => {
  const [nodes, setNodes] = React.useState<Node[]>([]);
  const [edges, setEdges] = React.useState<Edge[]>([]);
  const { slackChannels } = useFetchSlackChannels();
  const { workflows, loading: workflowsLoading, error: workflowsError, refetch: refetchWorkflows } = useFetchWorkflows();
  const { adminEmails } = useFetchAdminUsers();
  const [createTemplate] = useMutation(CREATE_TEMPLATE);

  // Handle ResizeObserver error
  React.useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message?.includes('ResizeObserver loop') || 
          e.message?.includes('ResizeObserver') ||
          e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message?.includes('loop limit exceeded') ||
          e.message?.includes('undelivered notifications')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      if (e.reason?.message?.includes('ResizeObserver')) {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Handler functions - declared early to avoid hoisting issues
  const handleTriggerChange = useCallback((value: string, nodeId: string) => {
    setNodes(nodes => nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            triggerType: value
          }
        };
      }
      return node;
    }));
  }, []);

  const handleConfigChange = useCallback((nodeId: string, config: any) => {
    setNodes(nodes => nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            config: {
              ...(typeof node.data.config === 'object' && node.data.config !== null ? node.data.config : {}),
              ...config
            }
          }
        };
      }
      return node;
    }));
  }, []);

  const handleToggleActive = useCallback((nodeId: string) => {
    setNodes(nodes => nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            active: node.data.active === false ? true : false
          }
        };
      }
      return node;
    }));
  }, []);

  // handleAddAction needs to be declared early but can't use useCallback 
  // because it depends on edges state which would cause re-renders
  const handleAddActionRef = React.useRef<(actionType: string, triggerId: string) => void>();
  
  const handleAddAction = (actionType: string, triggerId: string) => {
    handleAddActionRef.current?.(actionType, triggerId);
  };

  // Convert API workflow data to internal WorkflowData format
  const convertApiWorkflowsToInternal = useCallback((apiWorkflows: any[]) => {
    return apiWorkflows.map((workflow, index) => {
      const actions: NotificationAction[] = [];
      
      // Group recipients by channel type
      const emailRecipients = workflow.recipients
        .filter((r: any) => r.channel === 'GMAIL' || r.channel === 'email')
        .map((r: any) => r.recipientId);
      
      const slackRecipients = workflow.recipients
        .filter((r: any) => r.channel === 'SLACK' || r.channel === 'slack')
        .map((r: any) => r.recipientId);

      if (emailRecipients.length > 0) {
        actions.push({ type: 'email', list: emailRecipients });
      }
      
      if (slackRecipients.length > 0) {
        actions.push({ type: 'slack', list: slackRecipients });
      }

      return {
        triggerId: `workflow-${workflow.id}`,
        triggerType: workflow.eventType as NotificationEvent,
        actions: actions
      };
    });
  }, []);

  // Convert WorkflowData to ReactFlow format
  const convertWorkflowToReactFlow = useCallback((workflowData: WorkflowData[]) => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    let yOffset = 100;

    workflowData.forEach((workflow, index) => {
      const xOffset = index * 400 + 100;
      
      // Create trigger node
      const triggerNode: Node = {
        id: workflow.triggerId,
        type: 'triggerNode',
        position: { x: xOffset, y: yOffset },
        data: {
          triggerType: workflow.triggerType,
          active: true, // Default to active for new triggers
          onTriggerChange: (value: string) => handleTriggerChange(value, workflow.triggerId),
          onAddAction: (actionType: string) => handleAddAction(actionType, workflow.triggerId),
          onToggleActive: (nodeId: string) => handleToggleActive(nodeId),
          id: workflow.triggerId
        }
      };
      newNodes.push(triggerNode);

      // Create action nodes
      workflow.actions.forEach((action, actionIndex) => {
        const actionNodeId = `${action.type}-${workflow.triggerId}-${actionIndex}`;
        const actionNode: Node = {
          id: actionNodeId,
          type: 'actionNode',
          position: { x: xOffset, y: yOffset + 250 + (actionIndex * 200) },
          data: {
            label: action.type === 'email' ? 'Send Email' : 'Send Slack Message',
            type: action.type,
            config: { list: action.list },
            slackChannels: slackChannels,
            adminEmails: adminEmails,
            onConfigChange: (config: any) => handleConfigChange(actionNodeId, config)
          }
        };
        newNodes.push(actionNode);

        // Create edge
        const edge: Edge = {
          id: `${workflow.triggerId}-${actionNodeId}`,
          source: workflow.triggerId,
          target: actionNodeId,
          type: 'smoothstep'
        };
        newEdges.push(edge);
      });
    });

    return { nodes: newNodes, edges: newEdges };
  }, [slackChannels, adminEmails, handleTriggerChange, handleAddAction, handleToggleActive, handleConfigChange]);

  // Load workflows from API response
  useEffect(() => {
    if (workflows && workflows.length > 0) {
      const internalWorkflowData = convertApiWorkflowsToInternal(workflows);
      const { nodes: convertedNodes, edges: convertedEdges } = convertWorkflowToReactFlow(internalWorkflowData);
      setNodes(convertedNodes);
      setEdges(convertedEdges);
    }
  }, [workflows]); // Only depend on workflows changes

  // Update existing nodes with slackChannels and adminEmails when they become available
  useEffect(() => {
    if (slackChannels.length > 0 || adminEmails.length > 0) {
      setNodes(prevNodes => 
        prevNodes.map(node => {
          if (node.type === 'actionNode') {
            const updatedData = { ...node.data };
            
            if (node.data?.type === 'slack') {
              updatedData.slackChannels = slackChannels;
            }
            
            if (node.data?.type === 'email') {
              updatedData.adminEmails = adminEmails;
            }
            
            return {
              ...node,
              data: updatedData
            };
          }
          return node;
        })
      );
    }
  }, [slackChannels.length, adminEmails.length]); // Only depend on the length changes

  // Moved to after getWorkflowData definition to avoid hoisting issues

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback((params: any) => {
    // Only allow connecting from trigger to action nodes
    const sourceNode = nodes.find(node => node.id === params.source);
    const targetNode = nodes.find(node => node.id === params.target);
    
    if (sourceNode?.type === 'triggerNode' && targetNode?.type === 'actionNode') {
      setEdges((eds) => [...eds, { ...params, id: `${params.source}-${params.target}` }]);
    }
  }, [nodes]);

  // Assign the actual implementation to the ref
  React.useEffect(() => {
    handleAddActionRef.current = (actionType: string, triggerId: string) => {
      setNodes(nds => {
        const triggerNode = nds.find(n => n.id === triggerId);
        const existingActionNodes = nds.filter(n => n.type === 'actionNode' && 
          edges.some(e => e.source === triggerId && e.target === n.id)).length;
        const newNodeId = `${actionType}-${Date.now()}`;
        const newNode: Node = {
          id: newNodeId,
          type: 'actionNode',
          position: {
            x: triggerNode?.position.x || 100,
            y: (triggerNode?.position.y || 200) + 250 + (existingActionNodes * 200)
          },
          data: { 
            label: actionType === 'email' ? 'Send Email' : 'Send Slack Message',
            type: actionType,
            config: { list: [] },
            slackChannels: actionType === 'slack' ? slackChannels : undefined,
            adminEmails: actionType === 'email' ? adminEmails : undefined,
            onConfigChange: (config: any) => handleConfigChange(newNodeId, config)
          }
        };
        
        setEdges(eds => [...eds, {
          id: `${triggerId}-${newNode.id}`,
          source: triggerId,
          target: newNode.id,
          type: 'smoothstep'
        }]);
        
        return [...nds, newNode];
      });
    };
  }, [edges, slackChannels, adminEmails, handleConfigChange]);

  const addNewNode = useCallback((type: 'trigger' | 'email' | 'slack') => {
    if (type === 'trigger') {
      const newNodeId = `trigger-${Date.now()}`;
      const triggerNodes = nodes.filter(n => n.type === 'triggerNode');
      const xPos = triggerNodes.length * 400 + 100;
      
      const newNode: Node = {
        id: newNodeId,
        type: 'triggerNode',
        position: { x: xPos, y: 100 },
        data: {
          triggerType: '',
          active: true, // Default to active for new triggers
          onTriggerChange: (value: string) => handleTriggerChange(value, newNodeId),
          onAddAction: (actionType: string) => handleAddAction(actionType, newNodeId),
          onToggleActive: (nodeId: string) => handleToggleActive(nodeId),
          id: newNodeId
        }
      };
      setNodes((nds) => [...nds, newNode]);
    }
  }, [nodes, handleTriggerChange, handleAddAction, handleToggleActive]);

  const getWorkflowData = useCallback((): WorkflowData[] => {
    const triggerNodes = nodes.filter(node => node.type === 'triggerNode');
    
    return triggerNodes.map(trigger => {
      const connectedEdges = edges.filter(edge => edge.source === trigger.id);
      const actions = connectedEdges.map(edge => {
        const actionNode = nodes.find(n => n.id === edge.target);
        return {
          type: (actionNode?.data?.type || 'email') as 'email' | 'slack',
          list: (actionNode?.data?.config && typeof actionNode.data.config === 'object' && actionNode.data.config !== null && 'list' in actionNode.data.config) 
            ? (actionNode.data.config.list as string[]) || [] 
            : []
        };
      });

      return {
        triggerId: trigger.id,
        triggerType: (trigger.data?.triggerType || '') as NotificationEvent,
        actions
      };
    });
  }, [nodes, edges]);

  // Save workflow data to localStorage whenever it changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (nodes.length > 0) {
        const workflowData = getWorkflowData();
        localStorage.setItem('notification-workflow-data', JSON.stringify(workflowData));
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(debounceTimer);
  }, [nodes.length, edges.length]); // Only depend on the length changes


  // Convert workflow data to template format for the mutation
  const convertWorkflowToTemplateFormat = useCallback((): TemplateInput[] => {
    const workflowData = getWorkflowData();
    
    return workflowData.map(workflow => {
      const recipients: RecipientInput[] = [];
      
      // Get the trigger node to check if it's active
      const triggerNode = nodes.find(n => n.id === workflow.triggerId);
      const isWorkflowActive = triggerNode?.data?.active !== false;
      
      // Convert actions to recipients
      workflow.actions.forEach(action => {
        const actionNode = nodes.find(n => 
          n.type === 'actionNode' && 
          n.data?.type === action.type &&
          edges.some(e => e.source === workflow.triggerId && e.target === n.id)
        );
        
        // Add active recipients
        action.list.forEach(recipient => {
          recipients.push({
            recipientId: recipient,
            active: true,
            channel: action.type === 'email' ? 'GMAIL' : 'SLACK'
          });
        });
        
        // Add inactive recipients
        const actionConfig = actionNode?.data?.config as { list?: string[], inactiveList?: string[] } || {};
        const inactiveList = actionConfig.inactiveList || [];
        inactiveList.forEach((recipient: string) => {
          recipients.push({
            recipientId: recipient,
            active: false,
            channel: action.type === 'email' ? 'GMAIL' : 'SLACK'
          });
        });
      });
      
      return {
        eventType: workflow.triggerType,
        templateBody: '', // Can be extended to include custom message body
        active: isWorkflowActive,
        recipients: recipients
      };
    });
  }, [getWorkflowData, nodes, edges]);

  const handleSaveWorkflow = async () => {
    const workflowData = getWorkflowData();
    const templateData = convertWorkflowToTemplateFormat();
    
    console.log('Original workflow data:', workflowData);
    console.log('Template data for mutation:', templateData);
    console.log('API workflows data:', workflows);
    
    try {
      // Call the createTemplate mutation with template data
      const result = await createTemplate({ 
        variables: { input: templateData } 
      });
      
      console.log('Create template result:', result);
      
      // Refetch workflows after successful save
      refetchWorkflows();
      
      // Save to localStorage as backup
      localStorage.setItem('notification-workflow-data', JSON.stringify(workflowData));
      console.log('Workflow saved successfully!');
    } catch (error) {
      console.error('Failed to save workflow:', error);
      console.error('Error details:', error);
    }
  };

  return (
    <div style={{ width: '100%', height: '90vh' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        placeholder='Drag nodes here to build your workflow'
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}  // Set default viewport
        minZoom={0.1}  // Allow zooming out
        maxZoom={1.5}  // Limit maximum zoom
      >
        <Background />
        <Controls />
        <Panel position="top-left" style={{
          background: 'white',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          minWidth: '300px'
        }}>
          <button 
            onClick={() => addNewNode('trigger')}
            style={{
              backgroundColor: '#4caf50',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Add Trigger
          </button>
          <button 
            onClick={handleSaveWorkflow}
            style={{
              backgroundColor: '#2196f3',
              color: 'white',
              padding: '8px 16px',
              marginRight: '12px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Save Workflow
          </button>
        </Panel>
      </ReactFlow>
    </div>
  );
};